import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';
import SrTopicsModel from 'src/app/services/srTopics.model';
import { SrTopicsService } from 'src/app/services/sr-topics.service';

declare var $: any;

@Component({
  selector: 'app-sr-topics',
  templateUrl: './sr-topics.component.html',
  styleUrls: ['./sr-topics.component.scss']
})
export class SrTopicsComponent implements OnInit, OnDestroy {
  public srTopics: SrTopicsModel[] = [];
  public srTopicsList: SrTopicsModel[] = [];
  public srTopic: SrTopicsModel = new SrTopicsModel();
  public filteredSrTopics: SrTopicsModel[] = [];
  public search: string = '';
  public submitButton: boolean = false;
  public updateButton: boolean = false;
  public srTopicsDoubleClick: boolean = false;
  public NoDataMessage: boolean = false;
  public activePageNo: number;
  public registerForm: FormGroup;
  public submitted = false;
  public enableDisablePageLoader: boolean = false;
  public listLoader = false;
  public isFiltering: boolean = false;
  public noList: boolean = false;
  searchTimeout: any;
  private scrollTimeout: any;
  public formFieldCount: number = 0;
  public showForm: boolean = false;
  public loginUserName: string = '';

  private batchLoader: BatchLoader<any>;

  constructor(
    private toastrService: ToastrService,
    public formBuilder: FormBuilder,
    public srTopicsService: SrTopicsService,
    private locationStrategy: LocationStrategy,
    private previousRouteService: PreviousRouteService
  ) {
    this.srTopic = new SrTopicsModel();
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(30)]],
      description: ['', [Validators.required, Validators.maxLength(50)]]
    });
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.loginUserName = localStorage.getItem('LoginUserName') || '';
      this.enableDisablePageLoader = true;
      this.clear();
      this.search = '';
      this.srTopics = [];
      this.srTopicsList = [];
      this.filteredSrTopics = [];
      this.isFiltering = false;
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());

      this.srTopics = await this.srTopicsService.getAllSrTopics();

      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.enableDisablePageLoader = false;
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredSrTopics : this.srTopics,
      (updatedList) => {
        this.srTopicsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  get f() {
    return this.registerForm.controls;
  }

  public ngOnDestroy(): void {
    this.srTopicsService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.srTopics.filter(item =>
          item.name?.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query)
        )
      : this.srTopics;
    this.filteredSrTopics = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      this.noList = true;
      this.srTopicsList = [];
      return;
    }
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch();
    }, 300);
  }

  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.srTopic = new SrTopicsModel();
    this.srTopicsDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  public async addUpdateSrTopic() {
    this.submitted = true;
    if (this.registerForm.invalid) {
      return;
    }
    const formData = this.registerForm.value;
    const payload: any = {
      name: formData.name,
      description: formData.description,
      created_name: this.loginUserName,
      updated_name: this.loginUserName
    };

    try {
      this.srTopicsDoubleClick = true;
      if (this.submitButton) {
        await this.srTopicsService.create(payload);
        this.toastrService.success('Service Request Topic created successfully', '', {
          timeOut: 2000
        });
      } else {
        await this.srTopicsService.updateOneByID(this.srTopic.id, payload);
        this.toastrService.success('Service Request Topic updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.srTopicsDoubleClick = false;
    }
  }

  public getSrTopicById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.srTopicsDoubleClick = false;
    this.updateButton = true;
    const found = this.srTopicsList.find(item => item?.id === id);
    this.srTopic = found ? { ...found } : new SrTopicsModel();
    if (this.srTopic) {
      this.registerForm.patchValue({
        name: this.srTopic.name,
        description: this.srTopic.description,
      });
    }
  }

  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}

